/* @jsx h */
import { defineComponent, h, computed, ref } from "vue";
import {
  DateRange,
  DateRangePicker,
  DateRangeOption,
} from "@app/vue/components/UIComponents";
import { Colleague } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

const allDateRangeOptions: DateRangeOption[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const staffDateRangeOptions: DateRangeOption[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
];

export type ShiftFiltersProps = {
  filters: {
    userId?: number;
    dateRange: DateRange;
  };
  onFilterChange: (filters: ShiftFiltersProps["filters"]) => void;
  users: Colleague[];
  isStaff?: boolean;
  /** Called after Apply — lets PageHeader close the mobile sheet. */
  onApply?: () => void;
};

export const ShiftFilters = defineComponent<ShiftFiltersProps>({
  name: "ShiftFilters",
  props: ["filters", "onFilterChange", "users", "isStaff", "onApply"],
  setup(props) {
    const { t } = useLocalization();
    const dateRangeOptions = computed(() =>
      props.isStaff ? staffDateRangeOptions : allDateRangeOptions,
    );

    const set = (patch: Partial<ShiftFiltersProps["filters"]>) =>
      props.onFilterChange({ ...props.filters, ...patch });

    // Pending state for mobile — committed only on Apply.
    const pending = ref({ ...props.filters });

    const applySheet = () => {
      props.onFilterChange({ ...pending.value });
      props.onApply?.();
    };

    // ── Desktop: horizontal row ───────────────────────────────────────────────
    const renderDesktop = () => (
      <div class="flex items-end gap-2 flex-wrap">
        <DateRangePicker
          options={dateRangeOptions.value}
          presetsOnly={props.isStaff}
          value={props.filters.dateRange}
          onChange={(dateRange) => set({ dateRange })}
        />
        {!props.isStaff && props.users.length > 0 && (
          <select
            class="select select-bordered"
            value={props.filters.userId ?? ""}
            onChange={(e) =>
              set({
                userId: (e.target as HTMLSelectElement).value
                  ? Number((e.target as HTMLSelectElement).value)
                  : undefined,
              })
            }
          >
            <option value="">{t("reports.shifts.all_staff")}</option>
            {props.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.profile.name}
              </option>
            ))}
          </select>
        )}
      </div>
    );

    // ── Mobile: stacked in BottomSheet with pending state ────────────────────
    const renderMobileSheet = () => (
      <div class="flex flex-col gap-5 p-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.shifts.date_range")}
          </label>
          <DateRangePicker
            options={dateRangeOptions.value}
          presetsOnly={props.isStaff}
            value={pending.value.dateRange}
            onChange={(dateRange) =>
              (pending.value = { ...pending.value, dateRange })
            }
          />
        </div>
        {!props.isStaff && props.users.length > 0 && (
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
              {t("reports.shifts.staff_member")}
            </label>
            <select
              class="select select-bordered w-full"
              value={pending.value.userId ?? ""}
              onChange={(e) =>
                (pending.value = {
                  ...pending.value,
                  userId: (e.target as HTMLSelectElement).value
                    ? Number((e.target as HTMLSelectElement).value)
                    : undefined,
                })
              }
            >
              <option value="">{t("reports.shifts.all_staff")}</option>
              {props.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.profile.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div class="flex gap-2 pt-1">
          <button class="btn btn-primary btn-sm flex-1" onClick={applySheet}>
            <span class="material-symbols-outlined text-base">check</span>
            Apply
          </button>
        </div>
      </div>
    );

    return () => (
      <>
        <div class="hidden md:flex items-end gap-2 flex-wrap">
          {renderDesktop()}
        </div>
        <div class="md:hidden">{renderMobileSheet()}</div>
      </>
    );
  },
});
